import { PhoneNumberFormat, PhoneNumberUtil } from 'google-libphonenumber';

const phoneUtil = PhoneNumberUtil.getInstance();
const DEFAULT_REGION = 'US';

const toE164 = (parsed: ReturnType<typeof phoneUtil.parse>): string =>
  phoneUtil.format(parsed, PhoneNumberFormat.E164);

const tryParseUsNanp = (digits: string): string | null => {
  if (digits.length === 10) {
    try {
      const parsed = phoneUtil.parse(digits, DEFAULT_REGION);
      if (phoneUtil.isValidNumber(parsed)) {
        return toE164(parsed);
      }
    } catch {
      // fall through
    }
  }

  if (digits.length === 11 && digits.startsWith('1')) {
    try {
      const parsed = phoneUtil.parse(`+${digits}`);
      if (phoneUtil.isValidNumber(parsed)) {
        return toE164(parsed);
      }
    } catch {
      // fall through
    }
  }

  return null;
};

/**
 * Normalizes stored phone values for PhoneInput / react-international-phone.
 * Prevents US NANP numbers (e.g. 513-659-6001) from being misread as foreign
 * country codes (e.g. +51 Peru) when the leading area-code digit matches one.
 *
 * Non-US numbers stored without a "+" must NOT be forced to +1 — doing so
 * causes react-international-phone to switch the selected country to US while
 * the user is typing (e.g. Mexico +52 numbers during client edit).
 */
export const normalizePhoneNumberForInput = (phoneNumber: string): string => {
  const trimmed = phoneNumber.trim();
  if (!trimmed) {
    return '';
  }

  const digitsOnly = trimmed.replace(/\D/g, '');

  if (trimmed.startsWith('+')) {
    try {
      const parsed = phoneUtil.parse(trimmed);
      if (phoneUtil.isValidNumber(parsed)) {
        return toE164(parsed);
      }
    } catch {
      // fall through to US NANP retry below
    }

    const digitsAfterPlus = trimmed.slice(1).replace(/\D/g, '');
    // Only retry as US NANP when the value could plausibly be a +1 number.
    // Skip for numbers that clearly belong to another country (e.g. +52 Mexico).
    if (digitsAfterPlus.startsWith('1')) {
      const usNormalized = tryParseUsNanp(digitsAfterPlus);
      if (usNormalized) {
        return usNormalized;
      }
    }

    return trimmed;
  }

  const usFromDigits = tryParseUsNanp(digitsOnly);
  if (usFromDigits) {
    return usFromDigits;
  }

  try {
    const parsed = phoneUtil.parse(trimmed, DEFAULT_REGION);
    if (phoneUtil.isValidNumber(parsed)) {
      return toE164(parsed);
    }
  } catch {
    // fall through
  }

  if (digitsOnly) {
    return `+${digitsOnly}`;
  }

  return trimmed;
};
